/**
 * Çerezsiz birinci taraf toplu ziyaret sayımı (sunucu: server/olcum.ts, POST /api/olcum).
 *
 * Her sayfa görüntülemesinde (ilk yükleme + SPA rota değişimi) tek bir küçük istek gider: sayfa yolu (sorgu dizgisi ve
 * hash olmadan), bu sekmedeki giriş görüntülemesi mi, yalnız girişte yönlendiren sitenin alan adı, UTM kaynak/ortam/
 * kampanya ve reklam tıklaması türü (gclid → "google-ads"; kimliğin kendisi değil) ve kaba cihaz sınıfı (ekran
 * genişliğine göre mobil/masaüstü). Kimlik, rastgele kimlik, parmak izi gönderilmez; istek Referer başlığı ve çerez
 * taşımaz (bkz. gonder).
 *
 * Çerez, localStorage, sessionStorage ya da IndexedDB KULLANILMAZ: "giriş" bayrağı yalnız bu sayfanın belleğindedir.
 * Çerez panelindeki seçimden bağımsız çalışır; tarayıcı Global Privacy Control (GPC) sinyali gönderiyorsa hiç çalışmaz.
 * Yalnız üretim derlemesinde ve localhost dışında gönderilir. Bkz. Çerez Politikası, Gizlilik Politikası.
 */
import { yonlendirildiMi } from "./yonlendirmeler";

export const OLCUM_UCU = "/api/olcum";

type TiklamaTuru = "google-ads" | "meta" | "tiktok" | "microsoft-ads" | "linkedin";

export interface OlcumVerisi {
  yol: string;
  giris: boolean;
  kaynak?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  tik?: TiklamaTuru;
  cihaz: "mobil" | "masaustu";
}

const TIKLAMA_KIMLIKLERI: [string, TiklamaTuru][] = [
  ["gclid", "google-ads"], ["gbraid", "google-ads"], ["wbraid", "google-ads"],
  ["fbclid", "meta"], ["ttclid", "tiktok"], ["msclkid", "microsoft-ads"], ["li_fat_id", "linkedin"],
];
const YEREL = /^(localhost|127\.\d+\.\d+\.\d+|\[::1\]|0\.0\.0\.0)$|\.localhost$/;
/** Yönetim paneli ve müşteri dosya alanı sayılmaz */
const SAYILMAYAN = /^\/(admin|dosyalar)(\/|$)/;
const MOBIL_GENISLIK = 768;

/** Bu sayfa yüklemesinde giriş görüntülemesi işlendi mi (yalnız bellekte) */
let girisIslendi = false;

const kirp = (s: string, n: number) => s.replace(/[\u0000-\u001f\u007f]/g, " ").trim().slice(0, n);
const alan = (h: string) => h.toLowerCase().replace(/^www\./, "");

/** Sayım bu ortamda açık mı: üretim derlemesi, localhost değil, GPC yok */
export function olcumAcik(): boolean {
  try {
    if (!import.meta.env.PROD) return false;
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return false;
    return !YEREL.test(location.hostname);
  } catch {
    return false;
  }
}

/** Yüzde kodu çözülmüş, sorgu dizgisi/hash'siz, sonu eğik çizgisiz yol */
export function yolNormal(ham: string): string {
  let y = ham.split(/[?#]/)[0];
  try {
    y = decodeURI(y);
  } catch {
    /* geçersiz kodlama: olduğu gibi */
  }
  return kirp("/" + y.replace(/^\/+/, "").replace(/\/+$/, ""), 200);
}

function gezinmeTuru(): string {
  try {
    return (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type ?? "navigate";
  } catch {
    return "navigate";
  }
}

const cihaz = (): OlcumVerisi["cihaz"] => (window.innerWidth > 0 && window.innerWidth < MOBIL_GENISLIK ? "mobil" : "masaustu");

/** Girişte gelinen yer: yönlendiren alan adı (kendi alan adıysa boş), UTM ve tıklama türü */
function gelisBilgisi(): Pick<OlcumVerisi, "kaynak" | "utm_source" | "utm_medium" | "utm_campaign" | "tik"> & { siteIci: boolean } {
  let kaynak = "";
  let siteIci = false;
  if (document.referrer) {
    try {
      const h = alan(new URL(document.referrer).hostname);
      if (h === alan(location.hostname)) siteIci = true;
      else kaynak = h.slice(0, 100);
    } catch {
      /* geçersiz referrer */
    }
  }
  const p = new URLSearchParams(location.search);
  const utm = (a: string) => {
    const v = p.get(a);
    return v ? kirp(v, 100) || undefined : undefined;
  };
  const tik = TIKLAMA_KIMLIKLERI.find(([k]) => p.has(k))?.[1];
  return {
    siteIci,
    ...(kaynak ? { kaynak } : {}),
    ...(utm("utm_source") ? { utm_source: utm("utm_source") } : {}),
    ...(utm("utm_medium") ? { utm_medium: utm("utm_medium") } : {}),
    ...(utm("utm_campaign") ? { utm_campaign: utm("utm_campaign") } : {}),
    ...(tik ? { tik } : {}),
  };
}

/**
 * Tek yol fetch: sendBeacon'da Referer politikası verilemez ve tarayıcı aynı kaynağa giden isteğe sayfanın tam
 * adresini (sorgu dizgisi ve tıklama kimliği dahil) Referer olarak ekler. referrerPolicy "no-referrer" ile Referer
 * başlığı hiç gitmez; credentials "omit" ile çerez gitmez. Gövde düz metin (CORS ön kontrolü yok); sunucu JSON ayrıştırır.
 */
function gonder(v: OlcumVerisi) {
  try {
    void fetch(OLCUM_UCU, {
      method: "POST",
      body: JSON.stringify(v),
      keepalive: true,
      credentials: "omit",
      referrerPolicy: "no-referrer",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
    }).catch(() => {});
  } catch {
    /* ölçüm sayfayı asla bozmaz */
  }
}

/**
 * Her rota değişiminde çağrılır (App.tsx). İlk görüntüleme giriş sayılır; sayfa yenilemesi, geri/ileri ve site içinden
 * tam yükleme (ör. yeni sekme) giriş sayılmaz. Yönlendirme kaynağı yollar (istemci hemen hedefe geçer) sayılmaz.
 */
export function sayfaGoruntulendi(hamYol: string): void {
  try {
    if (!olcumAcik()) return;
    const yol = yolNormal(hamYol);
    if (SAYILMAYAN.test(yol) || yonlendirildiMi(yol)) return;
    const ilk = !girisIslendi;
    girisIslendi = true;
    const veri: OlcumVerisi = { yol, giris: false, cihaz: cihaz() };
    if (ilk) {
      const { siteIci, ...gelis } = gelisBilgisi();
      const tur = gezinmeTuru();
      if (!siteIci && tur !== "reload" && tur !== "back_forward") Object.assign(veri, { giris: true }, gelis);
    }
    gonder(veri);
  } catch {
    /* ölçüm sayfayı asla bozmaz */
  }
}
